"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AudioLines, Check, Plus, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { LOOP_WAVEFORM } from "./loop-constants";
import { useLoopClock, useLoopVisibility } from "./use-loop-clock";
import { StageCard } from "./StageCard";
import { TopicCreateDemo } from "./TopicCreateDemo";

/**
 * The hero's looping demonstration of a real practice session.
 *
 * This is the whole pitch in one card: the loop runs, and each stage
 * shows the actual product surface for that beat — a topic being
 * picked, a question being answered out loud with a live waveform,
 * three feedback notes, words landing in the deck, a streak filling.
 * It is a real React component with real Tailwind classes, not a
 * screenshot of one.
 *
 * Two rules keep it from becoming noise:
 *
 *   · The clock is shared with the loop diagram further down the page,
 *     so the two views never disagree (see use-loop-clock.ts).
 *   · The card never resizes. Every stage renders into a fixed-height
 *     frame, so the headline and the CTA above it stay exactly where
 *     they were while the content underneath cycles.
 *
 * The loop pauses while the pointer is over the card — someone reading
 * a stage should not have it yanked away mid-sentence.
 */

const QUESTION =
  "What small change could your city make this year to cut car trips in half?";

const FEEDBACK = [
  { tag: "Grammar", note: "Try “if… then” to link the two ideas." },
  {
    tag: "Vocabulary",
    note: "“Commute” and “pass” came up twice — both saved.",
  },
  {
    tag: "Pronunciation",
    note: "The stress on “halved” landed late. Try it slower.",
  },
];

const SAVED_WORDS = [
  { word: "commute", tier: "New" },
  { word: "pass", tier: "New" },
  { word: "wider", tier: "Learning" },
  { word: "halved", tier: "Learning" },
];

const WEEK = [true, true, false, true, true, true, false];

export function PracticeLoop() {
  const reduceMotion = useReducedMotion();
  const { stage, index, stages, running, setPaused } = useLoopClock();
  const visibilityRef = useLoopVisibility<HTMLDivElement>();

  const [paused, setLocalPaused] = useState(false);

  // Keep the clock's idea of "paused" in step with the local hover state.
  useEffect(() => {
    setPaused(paused);
    return () => setPaused(false);
  }, [paused, setPaused]);

  return (
    <div
      ref={visibilityRef}
      onPointerEnter={() => setLocalPaused(true)}
      onPointerLeave={() => setLocalPaused(false)}
      onFocusCapture={() => setLocalPaused(true)}
      onBlurCapture={() => setLocalPaused(false)}
      className="relative"
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: reduceMotion ? 0 : 0.7,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1],
        }}
        role="img"
        aria-label={`A practice session looping through ${stages.length} stages: ${stages
          .map((s) => s.label)
          .join(", ")}`}
        className={cn(
          "relative w-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] p-5",
          "shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-6",
        )}
      >
        <div
          className="loop-sheen pointer-events-none absolute inset-0"
          style={{ "--sheen-delay": "2s" } as React.CSSProperties}
        />

        {/* Header: live dot + position in the loop */}
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2 text-emerald-400">
              <span className="loop-ping absolute inset-0 rounded-full" />
              <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
              Live session
            </span>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[10px] font-medium tabular-nums text-white/45">
            {String(index + 1).padStart(2, "0")} /{" "}
            {String(stages.length).padStart(2, "0")}
          </span>
        </div>

        {/* The frame — fixed height, so nothing on the page shifts. */}
        <div className="relative mt-5 h-[286px] sm:h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                duration: reduceMotion ? 0 : 0.45,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="absolute inset-0"
            >
              {stage.id === "topic" && <TopicCreateDemo />}
              {stage.id === "answer" && <AnswerStage />}
              {stage.id === "feedback" && <FeedbackStage />}
              {stage.id === "words" && <WordsStage />}
              {stage.id === "progress" && <ProgressStage />}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress rail — one segment per stage, filling over the dwell.
            Under reduced motion nothing fills and nothing turns. */}
        <div
          className="relative mt-5 flex items-center gap-1.5"
          aria-hidden="true"
        >
          {stages.map((s, i) => (
            <span
              key={s.id}
              className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/10"
            >
              <span
                className={cn(
                  "block h-full w-full origin-left",
                  !reduceMotion && i === index && "loop-fill",
                  i === index && "bg-gradient-to-r from-primary to-secondary",
                )}
                style={
                  { "--loop-dwell": `${s.dwell}ms` } as React.CSSProperties
                }
              />
            </span>
          ))}
        </div>

        {/* Caption — the one line of information this beat carries. */}
        <div className="relative mt-3.5 flex h-10 items-start gap-2">
          <AnimatePresence mode="wait">
            <motion.p
              key={stage.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: reduceMotion ? 0 : 0.35 }}
              className="text-[12px] leading-relaxed text-white/45"
            >
              <span className="font-semibold text-white/75">{stage.label}</span>
              <span className="mx-1.5 text-white/25">—</span>
              {stage.caption}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Pause state is visible to assistive tech, so a held frame never
            reads as a stuck animation. */}
        <span className="sr-only" aria-live="polite">
          {paused || !running
            ? "Session demo paused"
            : `Showing ${stage.label}`}
        </span>
      </motion.div>
    </div>
  );
}

/* ── Stages ─────────────────────────────────────────────────── */

const ThinkingDots = () => (
  <span className="flex h-5 items-center gap-1" aria-label="Tutor is thinking">
    {[0, 1, 2].map((dot) => (
      <span
        key={dot}
        className="loop-typing h-1.5 w-1.5 rounded-full bg-white/70"
        style={{ animationDelay: `${dot * 0.16}s` }}
      />
    ))}
  </span>
);

function AnswerStage() {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setSeconds((s) => Math.min(s + 1, 18)),
      1000,
    );
    return () => window.clearInterval(id);
  }, []);

  return (
    <StageCard className="flex flex-col">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-white shadow-lg shadow-primary/20">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <p className="rounded-2xl rounded-tl-sm bg-white/[0.07] px-3.5 py-2.5 text-[13px] leading-relaxed text-white/90">
          {QUESTION}
        </p>
      </div>

      <div className="mt-3 flex items-start justify-end gap-2.5">
        <div className="max-w-[82%] rounded-2xl rounded-tr-sm border border-primary/25 bg-primary/15 px-3.5 py-2.5">
          <ThinkingDots />
        </div>
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/70">
          <AudioLines className="h-3.5 w-3.5" />
        </span>
      </div>

      {/* Waveform + running clock — only alive while the answer plays. */}
      <div className="mt-auto">
        <div
          className="flex h-9 items-end justify-center gap-[3px]"
          aria-hidden="true"
        >
          {LOOP_WAVEFORM.map((height, i) => (
            <span
              key={i}
              className="loop-wave w-[3px] rounded-full bg-gradient-to-t from-primary/70 to-secondary"
              style={
                {
                  height: `${height}px`,
                  "--d": `${(i % 9) * 0.11}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-white/45">
          <span className="relative flex h-1.5 w-1.5 text-rose-400">
            <span className="loop-ping absolute inset-0 rounded-full" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-rose-400" />
          </span>
          Recording
          <span className="font-mono tabular-nums text-white/70">
            00:{String(seconds).padStart(2, "0")}
          </span>
        </div>
      </div>
    </StageCard>
  );
}

function FeedbackStage() {
  return (
    <StageCard className="flex flex-col">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-secondary">
        Feedback
      </p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">
        “Wider bike lanes and a bus lane, with the monthly pass halved.”
      </p>
      <ul className="mt-3.5 space-y-1.5">
        {FEEDBACK.map((line, i) => (
          <li
            key={line.tag}
            className="loop-pop rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5"
            style={{ animationDelay: `${0.1 + i * 0.16}s` }}
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
              {line.tag}
            </p>
            <p className="mt-0.5 text-[12px] leading-snug text-white/65">
              {line.note}
            </p>
          </li>
        ))}
      </ul>
    </StageCard>
  );
}

function WordsStage() {
  return (
    <StageCard className="flex flex-col">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">
          Saved to your deck
        </p>
        <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium text-emerald-200">
          +4 words
        </span>
      </div>
      <ul className="mt-3.5 space-y-2">
        {SAVED_WORDS.map((entry, i) => (
          <li
            key={entry.word}
            className="loop-pop flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2"
            style={{ animationDelay: `${0.12 + i * 0.13}s` }}
          >
            <span className="font-display text-[15px] font-semibold text-white">
              {entry.word}
            </span>
            <span className="flex items-center gap-2">
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/60">
                {entry.tier}
              </span>
              <Plus className="h-3 w-3 text-emerald-300/70" />
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-auto pt-4 text-[11px] text-white/35">
        These come back in a later session, spaced out.
      </p>
    </StageCard>
  );
}

function ProgressStage() {
  return (
    <StageCard className="flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">
            This week
          </p>
          <p className="mt-1 font-display text-2xl font-semibold text-white">
            5 of 7 days
          </p>
        </div>
        <span className="rounded-full border border-secondary/25 bg-secondary/15 px-2.5 py-1 text-[11px] font-semibold text-secondary">
          +40 XP
        </span>
      </div>

      <div className="mt-auto flex items-end justify-between gap-2 pt-6">
        {WEEK.map((active, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-2">
            <span
              className={cn(
                "loop-rise block h-14 w-full rounded-md",
                active
                  ? "bg-gradient-to-t from-primary to-blue-400"
                  : "bg-white/10",
              )}
              style={{ animationDelay: `${0.1 + i * 0.08}s` }}
            />
            <span className="text-[9px] uppercase tracking-wider text-white/30">
              {["M", "T", "W", "T", "F", "S", "S"][i]}
            </span>
          </div>
        ))}
      </div>
    </StageCard>
  );
}
